import React, { Dispatch, FC, SetStateAction, useCallback } from 'react';
import {
  FormLayout,
  FormNodeNameType,
  FormNodeType,
  FormPageModel,
  FormSectionModel,
} from '../../../../shared/kiosk';
import { nodeEditors } from './elements';
import { ToolboxItem } from './models/ToolboxItem';

export type ToolboxMode = 'closed' | 'pages' | 'toolbox';

export interface RootFormState {
  toolboxTab: ToolboxMode;
  activePage: string | null;
  layout: FormLayout;
}

export type SetRoot = Dispatch<SetStateAction<RootFormState>>;
export type SetPage = Dispatch<SetStateAction<FormPageModel>>;
export type SetPageSection = (sectionId: string, fun: SetStateAction<FormSectionModel>) => void;

export function usePage(
  state: RootFormState,
  pageId: string,
  setState: SetRoot,
): [FormPageModel | undefined, SetPage] {
  const page = state.layout.pages.find((p) => p.id === pageId);

  const setPage = useCallback(
    (newPage: SetStateAction<FormPageModel>) => {
      setState((oldState) => {
        return {
          ...oldState,
          layout: {
            ...oldState.layout,
            pages: oldState.layout.pages.map((p) => {
              if (p.id === pageId) {
                return typeof newPage === 'function' ? newPage(p) : newPage;
              }

              return p;
            }),
          },
        };
      });
    },
    [setState, pageId],
  );

  return [page, setPage];
}

export function useSetSection(setPage: SetPage): SetPageSection {
  return useCallback(
    (sectionId: string, newSection: SetStateAction<FormSectionModel>) => {
      setPage((oldPage) => ({
        ...oldPage,
        sections: oldPage.sections.map((s) => {
          if (sectionId === s.id) {
            return typeof newSection === 'function' ? newSection(s) : newSection;
          }

          return s;
        }),
      }));
    },
    [setPage],
  );
}

export function useActivePage(
  state: RootFormState,
  setState: SetRoot,
): [FormPageModel | undefined, SetPage] {
  return usePage(state, state.activePage!, setState);
}

interface EditorRendererProps {
  readonly type: FormNodeNameType;
  readonly node?: FormNodeType;
  readonly isDragOverlay?: boolean;
  readonly nextId: number;
}

export const EditorNodeRenderer: FC<EditorRendererProps> = ({
  type,
  node,
  isDragOverlay,
  nextId,
}) => {
  const details = nodeEditors[type];

  return (
    <ToolboxItem
      description={details.getDescription(node)}
      icon={details.getIcon()}
      id={`toolbox-${type}`}
      isDragOverlay={isDragOverlay}
      name={details.getName()}
      nextId={nextId}
      nodeType={type}
    />
  );
};
